import { useActiveProject } from '@/hooks/useActiveProject'
import { isModalOpen } from '@/utils/isModalOpen'

import { UseReorderReturnProps } from './reorder.types'
import {
	clearDragRowIndicators,
	getDragPosition,
	shouldSkipDrop,
} from './reorder.utils'

export const useReorder = (track: Track): UseReorderReturnProps => {
	const {
		tracks: projectTracks,
		isEditable,
		reorderTracks,
	} = useActiveProject()

	const enableDrop = isEditable

	const handleDragOver = (event: React.DragEvent<HTMLElement>): void => {
		event.preventDefault()

		if (isModalOpen()) {
			return
		}

		const dragTarget: HTMLElement = (event.target as Element).closest(
			'[draggable]'
		)

		if (!dragTarget) {
			return
		}

		const rect = dragTarget.getBoundingClientRect()
		const position = getDragPosition(rect, event.clientY)
		const goesAbove = position === 'above'

		const { classList, dataset } = dragTarget

		if (
			(goesAbove && classList.contains('trackRowDragAbove')) ||
			(!goesAbove && classList.contains('trackRowDragBelow'))
		) {
			return
		}

		classList.remove('trackRowDragAbove', 'trackRowDragBelow')
		classList.add(goesAbove ? 'trackRowDragAbove' : 'trackRowDragBelow')
		dataset.dragPosition = position
	}

	const handleDragEnter = (event: React.DragEvent<HTMLElement>): void => {
		event.preventDefault()
		event.dataTransfer.effectAllowed = 'move'
	}

	const handleDragLeave = (event: React.DragEvent<HTMLElement>): void => {
		const dragTarget: HTMLElement = (event.target as Element).closest(
			'[draggable]'
		)

		if (!dragTarget) {
			return
		}

		dragTarget.classList.remove('trackRowDragAbove', 'trackRowDragBelow')
		delete dragTarget.dataset.dragPosition
	}

	const handleDragEnd = (): void => {
		clearDragRowIndicators()
	}

	const handleDrop = (event: React.DragEvent<HTMLElement>): void => {
		if (isModalOpen()) {
			return
		}

		event.preventDefault()

		const target: HTMLElement = (event.target as Element).closest('[draggable]')

		const draggedTrack = JSON.parse(event.dataTransfer.getData('text/plain'))

		const goesAbove = target.dataset.dragPosition === 'above'
		const goesBelow = target.dataset.dragPosition === 'below'

		const oldPosition = projectTracks.findIndex(
			(projectTrack) => projectTrack.id === draggedTrack.id
		)
		const newPosition = projectTracks.findIndex(
			(projectTrack) => projectTrack.id === track.id
		)

		if (shouldSkipDrop(oldPosition, newPosition, goesAbove, goesBelow)) {
			clearDragRowIndicators()
			return
		}

		clearDragRowIndicators()

		if (oldPosition === -1) {
			const insertNewTrackAtIndex = goesAbove ? newPosition : newPosition + 1
			reorderTracks?.(
				oldPosition,
				insertNewTrackAtIndex,
				'Drag',
				draggedTrack.id
			)
		} else {
			reorderTracks?.(oldPosition, newPosition, 'Drag')
		}
	}

	return {
		handleDragOver: enableDrop ? handleDragOver : null,
		handleDragEnter: enableDrop ? handleDragEnter : null,
		handleDragLeave: enableDrop ? handleDragLeave : null,
		handleDragEnd: enableDrop ? handleDragEnd : null,
		handleDrop: enableDrop ? handleDrop : null,
	}
}
